import { useEffect, useState } from "react";
import { Loader2, GraduationCap, Check, Circle, PlayCircle, Trophy } from "lucide-react";
import api from "../lib/api";

const STATUS_META = {
  completed: {
    label: "Completato",
    icon: Check,
    dot: "bg-emerald-400 border-emerald-300 text-[#0A1F18]",
    line: "bg-emerald-400/60",
    text: "text-emerald-300",
  },
  in_progress: {
    label: "In corso",
    icon: PlayCircle,
    dot: "bg-sheriff-gold border-sheriff-gold text-[#0A1F18] animate-pulse",
    line: "bg-sheriff-gold/50",
    text: "text-sheriff-gold",
  },
  pending: {
    label: "In attesa",
    icon: Circle,
    dot: "bg-[#143830] border-sheriff-gold/40 text-sheriff-gold/60",
    line: "bg-sheriff-gold/15",
    text: "text-sheriff-gold/60",
  },
};

function StepIcon({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <div className={`h-10 w-10 rounded-full border-2 flex items-center justify-center shrink-0 ${meta.dot}`}>
      <Icon className="h-5 w-5" />
    </div>
  );
}

export default function FormazioneTimeline({ onLoaded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/training/my-progress")
      .then(({ data }) => {
        if (cancelled) return;
        setData(data);
        onLoaded && onLoaded(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Impossibile caricare il percorso formativo");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [onLoaded]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-14" data-testid="formazione-loading">
        <Loader2 className="h-6 w-6 animate-spin text-sheriff-gold" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6 text-red-300" data-testid="formazione-error">
        {error}
      </div>
    );
  }
  if (!data || !data.timeline?.length) {
    return (
      <div className="rounded-2xl border border-dashed border-sheriff-gold/20 p-10 text-center text-sheriff-gold/60">
        Nessuno step formativo configurato. Contatta le Risorse Umane.
      </div>
    );
  }

  const complete = data.completed_steps === data.total_steps && data.total_steps > 0;

  return (
    <div className="space-y-6" data-testid="formazione-timeline">
      {/* HEADER + PROGRESS */}
      <div className="rounded-2xl border border-sheriff-gold/30 bg-gradient-to-br from-sheriff-gold/10 to-[#0F2620] p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="uppercase-label text-sheriff-gold mb-1 flex items-center gap-2">
              <GraduationCap className="h-4 w-4" /> Il tuo percorso formativo
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl text-[#F5E6B8] tracking-tighter">
              {complete ? "Percorso completato" : "Formazione in corso"}
              {complete && <Trophy className="inline-block ml-2 h-6 w-6 text-sheriff-gold" />}
            </h2>
          </div>
          <div className="text-right">
            <div className="dossier-mono text-xs uppercase tracking-wider text-sheriff-gold/60">Avanzamento</div>
            <div className="font-heading text-4xl text-sheriff-gold" data-testid="formazione-percent">{data.percent}%</div>
            <div className="text-xs text-[#D4C480] mt-0.5">
              {data.completed_steps} / {data.total_steps} step
            </div>
          </div>
        </div>
        <div className="mt-4 h-2 w-full bg-[#0A1F18] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-sheriff-gold/70 to-sheriff-gold transition-all duration-500"
            style={{ width: `${data.percent}%` }}
          />
        </div>
      </div>

      {/* TIMELINE */}
      <ol className="relative" data-testid="formazione-steps">
        {data.timeline.map((step, idx) => {
          const meta = STATUS_META[step.status] || STATUS_META.pending;
          const isLast = idx === data.timeline.length - 1;
          return (
            <li key={step.id} className="relative pl-14 pb-6 last:pb-0" data-testid={`formazione-step-${step.order}`}>
              {/* linea verticale */}
              {!isLast && (
                <span className={`absolute left-[19px] top-11 bottom-0 w-[2px] ${meta.line}`} aria-hidden />
              )}
              {/* pallino */}
              <span className="absolute left-0 top-0">
                <StepIcon status={step.status} />
              </span>
              {/* contenuto */}
              <div className={`rounded-xl border p-4 transition-all ${
                step.status === "completed" ? "border-emerald-400/25 bg-emerald-400/[0.03]"
                : step.status === "in_progress" ? "border-sheriff-gold/50 bg-sheriff-gold/[0.05]"
                : "border-sheriff-gold/20 bg-[#143830]/40"
              }`}>
                <div className="flex items-baseline gap-3 flex-wrap mb-1">
                  <span className="dossier-mono text-[10px] uppercase tracking-[0.2em] text-sheriff-gold/50">
                    Step {String(idx + 1).padStart(2, "0")}
                  </span>
                  {step.category && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-sheriff-gold/10 border border-sheriff-gold/30 text-sheriff-gold/80">
                      {step.category}
                    </span>
                  )}
                  <span className={`text-[10px] uppercase tracking-wider ml-auto ${meta.text}`}>
                    {meta.label}
                  </span>
                </div>
                <h3 className="font-heading text-lg text-[#F5E6B8] mb-1">{step.name}</h3>
                {step.description && (
                  <p className="text-sm text-[#D4C480] leading-relaxed">{step.description}</p>
                )}
                {step.note && (
                  <div className="mt-3 border-l-2 border-sheriff-gold/40 bg-sheriff-gold/[0.04] pl-3 py-2 text-xs">
                    <div className="dossier-mono text-[10px] uppercase tracking-wider text-sheriff-gold/60 mb-1">
                      Nota HR{step.updated_by_name ? ` · ${step.updated_by_name}` : ""}
                    </div>
                    <div className="text-[#D4C480]">{step.note}</div>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
